/**
 * SettingsPage.tsx
 *
 * User profile settings — matches legacy settings_user_details.php functionality.
 * Three tabs: Profile, Password, Notifications.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { User, Lock, Bell, Save, Check, Upload, Calendar, Nfc, Link as LinkIcon, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { apiClient, getErrorMessage } from '@/services/apiClient';
import { cn } from '@/lib/utils';
import { TiptapEditor } from '@/v5/components/ui/tiptap-editor';
import { UserAvatar } from '@/components/users/UserAvatar';
import {
  getGoogleCalendarSettings,
  getGoogleCalendarOAuthUrl,
  listGoogleCalendars,
  updateGoogleCalendarSettings,
  syncGoogleCalendarNow,
  disconnectGoogleCalendar,
  type GoogleCalendarFlags,
} from '@/services/googleCalendar.service';
import { FrontDeskReaderTab } from '@/components/settings/FrontDeskReaderTab';

type Tab = 'profile' | 'password' | 'notifications' | 'calendar' | 'frontdesk';

const TABS: { key: Tab; label: string; icon: typeof User }[] = [
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'password', label: 'Password', icon: Lock },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'calendar', label: 'Calendar Sync', icon: Calendar },
  { key: 'frontdesk', label: 'Front Desk Reader', icon: Nfc },
];

interface UserProfile {
  userID: number;
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  suffix: string;
  title: string;
  userLevel: number;
  role: string;
  signatureEnabled: string;
  emailSignature: string;
  image: string;
  thumb: string;
  avatarUrl: string | null;
}

interface NotificationPreferences {
  receivedLead: string;
  receivedLeadassign: string;
  receivedLab: string;
  receivedEmail: string;
  receivedSms: string;
  receivedMessage: string;
  paymentDeclined: string;
  paymentReceived: string;
  appointmentStatus: string;
  receivedConsult: string;
  receivedQa: string;
  ccOnSystemReplies: string;
  rescheduleRequest: string;
  noteAlert: string;
  eformAlert: string;
  clearanceAlert: string;
  surgeonNoteAlert: string;
  /** Axiom replies to a pharmacy support ticket (SCRUM-1994). */
  axiomSupport: string;
  /** A membership was cancelled anywhere in the system. */
  membershipCancelled: string;
}

interface PhotoUploadPayload {
  file: File;
  resizedImage?: File;
  resizedThumb?: File;
}

const NOTIF_DEFAULTS: NotificationPreferences = {
  receivedLead: '0', receivedLeadassign: '0', receivedLab: '0',
  receivedEmail: '0', receivedSms: '0', receivedMessage: '0',
  paymentDeclined: '0', paymentReceived: '0', appointmentStatus: '0',
  receivedConsult: '0', receivedQa: '0', ccOnSystemReplies: '0',
  rescheduleRequest: '0', noteAlert: '0', eformAlert: '0',
  clearanceAlert: '0', surgeonNoteAlert: '0',
  // On by default: the reply alert reached its addressee before it was a choice.
  axiomSupport: '1',
  // New alert, so off until the user turns it on.
  membershipCancelled: '0',
};

const NOTIF_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '1', label: 'Alert' },
  { value: '2', label: 'Alert & E-mail' },
];

const CC_OPTIONS = [
  { value: '1', label: 'Only for manually sent messages' },
  { value: '0', label: 'In all followed plans' },
];

const GCAL_FLAG_DEFAULTS: GoogleCalendarFlags = {
  showPatientName: false, showPatientID: false, showPlanID: false, showApptName: false,
  showApptType: false, showApptNotes: false, showResources: false, showPlanLink: false,
};

// The 8 legacy "included details" toggles, in the legacy display order.
const GCAL_FLAG_LABELS: [keyof GoogleCalendarFlags, string][] = [
  ['showApptName', 'Appointment name'],
  ['showApptType', 'Appointment type'],
  ['showApptNotes', 'Appointment notes'],
  ['showPatientName', 'Patient name'],
  ['showPatientID', 'Patient ID'],
  ['showPlanID', 'Plan ID'],
  ['showResources', 'Resources'],
  ['showPlanLink', 'Plan link'],
];

async function resizePhotoToJpeg(file: File, targetWidth: number, filename: string): Promise<File> {
  const imageUrl = URL.createObjectURL(file);

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Unable to read image'));
      element.src = imageUrl;
    });

    const sourceWidth = img.naturalWidth || img.width;
    const sourceHeight = img.naturalHeight || img.height;
    if (!sourceWidth || !sourceHeight) {
      throw new Error('Invalid image dimensions');
    }

    const targetHeight = Math.max(1, Math.round(sourceHeight * (targetWidth / sourceWidth)));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas is not available');
    }

    context.fillStyle = '#fff';
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.drawImage(img, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error('Unable to resize image'));
      }, 'image/jpeg', 0.9);
    });

    return new File([blob], filename, { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1">
      {children}
    </label>
  );
}

function TextInput({ id, value, onChange, disabled, type = 'text', placeholder }: {
  id: string; value: string; onChange?: (v: string) => void; disabled?: boolean; type?: string; placeholder?: string;
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      disabled={disabled}
      placeholder={placeholder}
      className={cn(
        'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
        disabled ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-slate-300',
      )}
    />
  );
}

function SelectInput({ id, value, onChange, options }: {
  id: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function SuccessBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
      <Check className="h-4 w-4 flex-shrink-0" /> {message}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border bg-red-50 border-red-200 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

export function SettingsPage() {
  const updateAuthUser = useAuthStore((s) => s.updateUser);
  const accountId = useAuthStore((s) => s.accountId);
  const [tab, setTab] = useState<Tab>(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    if (requested === 'calendar') return 'calendar';
    if (requested === 'frontdesk') return 'frontdesk';
    return 'profile';
  });
  const queryClient = useQueryClient();
  const photoInputRef = useRef<HTMLInputElement>(null);

  // ─── Profile Data ──────────────────────────────────────────────────
  const { data: profileData, isLoading: profileLoading } = useQuery<{ data: UserProfile }>({
    queryKey: ['user-profile'],
    queryFn: () => apiClient.get('/user/profile').then((r) => r.data),
  });
  const profile = profileData?.data;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [suffix, setSuffix] = useState('');
  const [title, setTitle] = useState('');
  const [signatureEnabled, setSignatureEnabled] = useState('0');
  const [emailSignature, setEmailSignature] = useState('');
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [photoMsg, setPhotoMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstName);
      setLastName(profile.lastName);
      setEmail(profile.email);
      setPhone(profile.phone);
      setSuffix(profile.suffix);
      setTitle(profile.title);
      setSignatureEnabled(profile.signatureEnabled);
      setEmailSignature(profile.emailSignature);
    }
  }, [profile]);

  const profileMutation = useMutation({
    mutationFn: () =>
      apiClient.put('/user/profile', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        suffix: suffix.trim(),
        title: title.trim(),
        signatureEnabled,
        emailSignature,
      }),
    onSuccess: () => {
      updateAuthUser({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), displayName: `${firstName.trim()} ${lastName.trim()}`.trim() });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      setProfileMsg('Profile updated successfully.');
      setTimeout(() => setProfileMsg(null), 4000);
    },
  });

  const photoMutation = useMutation({
    mutationFn: (payload: PhotoUploadPayload) => {
      const formData = new FormData();
      formData.append('file', payload.file);
      if (payload.resizedImage && payload.resizedThumb) {
        formData.append('resizedImage', payload.resizedImage);
        formData.append('resizedThumb', payload.resizedThumb);
      }
      return apiClient.post<{ data: { image: string; thumb: string; avatarUrl: string | null } }>('/user/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (response) => {
      const photo = response.data.data;
      const avatarUrl = photo?.avatarUrl ?? null;
      updateAuthUser({ avatarUrl });
      queryClient.setQueryData<UserProfile | undefined>(['user-profile'], (current) =>
        current
          ? {
              ...current,
              image: photo?.image ?? current.image,
              thumb: photo?.thumb ?? current.thumb,
              avatarUrl,
            }
          : current,
      );
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['navigation', 'main', accountId] });
      setPhotoMsg({ type: 'success', text: 'Photo uploaded successfully.' });
      setTimeout(() => setPhotoMsg(null), 4000);
    },
    onError: (error) => {
      setPhotoMsg({ type: 'error', text: getErrorMessage(error, 'Failed to upload photo.') });
    },
  });

  async function handlePhotoSelected(file: File | undefined) {
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setPhotoMsg({ type: 'error', text: 'Please upload a JPG, PNG, or GIF image.' });
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setPhotoMsg({ type: 'error', text: 'Photo must be 15 MB or smaller.' });
      return;
    }

    try {
      const [resizedImage, resizedThumb] = await Promise.all([
        resizePhotoToJpeg(file, 408, 'user-photo.jpg'),
        resizePhotoToJpeg(file, 99, 'user-photo-thumb.jpg'),
      ]);
      photoMutation.mutate({ file, resizedImage, resizedThumb });
    } catch {
      photoMutation.mutate({ file });
    }
  }

  // ─── Password ──────────────────────────────────────────────────────
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const passwordMutation = useMutation({
    mutationFn: () => apiClient.put('/user/password', { newPassword: newPw, confirmPassword: confirmPw }),
    onSuccess: () => {
      setNewPw('');
      setConfirmPw('');
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setTimeout(() => setPwMsg(null), 4000);
    },
    onError: () => {
      setPwMsg({ type: 'error', text: 'Failed to change password.' });
    },
  });

  // ─── Notification Preferences ──────────────────────────────────────
  const { data: notifData, isLoading: notifLoading } = useQuery<{ data: NotificationPreferences }>({
    queryKey: ['user-notification-preferences'],
    queryFn: () => apiClient.get('/user/notification-preferences').then((r) => r.data),
  });

  const [notifs, setNotifs] = useState<NotificationPreferences>(NOTIF_DEFAULTS);
  // Only practices that order from Axiom (pens or vials) see its row.
  const axiomPens = useModuleAccess('axiom');
  const axiomVials = useModuleAccess('axiom vials');
  const showAxiomSupport = axiomPens.explicit || axiomVials.explicit;
  // Only practices with memberships see the Membership Cancelled row.
  const showMembershipCancelled = useModuleAccess('memberships').enabled;
  const [notifMsg, setNotifMsg] = useState<string | null>(null);

  useEffect(() => {
    if (notifData?.data) {
      setNotifs(notifData.data);
    }
  }, [notifData]);

  const setNotif = (key: keyof NotificationPreferences, value: string) => {
    setNotifs((prev) => ({ ...prev, [key]: value }));
  };

  const notifMutation = useMutation({
    mutationFn: () => apiClient.put('/user/notification-preferences', notifs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-notification-preferences'] });
      setNotifMsg('Notification preferences saved.');
      setTimeout(() => setNotifMsg(null), 4000);
    },
  });

  // ─── Google Calendar Sync ──────────────────────────────────────────
  const [gcalCalendarId, setGcalCalendarId] = useState('');
  const [gcalFlags, setGcalFlags] = useState<GoogleCalendarFlags>(GCAL_FLAG_DEFAULTS);
  const [gcalMsg, setGcalMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: gcalSettings, isLoading: gcalLoading } = useQuery({
    queryKey: ['gcal-settings'],
    queryFn: getGoogleCalendarSettings,
  });
  const gcalConnected = gcalSettings?.connected ?? false;

  useEffect(() => {
    if (gcalSettings) {
      setGcalCalendarId(gcalSettings.calendarId);
      setGcalFlags(gcalSettings.flags);
    }
  }, [gcalSettings]);

  const { data: gcalCalendars, isLoading: gcalCalendarsLoading } = useQuery({
    queryKey: ['gcal-calendars'],
    queryFn: listGoogleCalendars,
    enabled: gcalConnected,
  });

  const gcalConnectMutation = useMutation({
    mutationFn: getGoogleCalendarOAuthUrl,
    onSuccess: (url) => { window.location.href = url; },
    onError: (e) => setGcalMsg({ type: 'error', text: getErrorMessage(e, 'Could not start the Google connection.') }),
  });

  const gcalSaveMutation = useMutation({
    mutationFn: () => updateGoogleCalendarSettings({ calendarId: gcalCalendarId, ...gcalFlags }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gcal-settings'] });
      setGcalMsg({ type: 'success', text: 'Calendar sync settings saved.' });
      setTimeout(() => setGcalMsg(null), 4000);
    },
    onError: (e) => setGcalMsg({ type: 'error', text: getErrorMessage(e, 'Failed to save settings.') }),
  });

  const gcalSyncMutation = useMutation({
    mutationFn: syncGoogleCalendarNow,
    onSuccess: (r) =>
      setGcalMsg({ type: 'success', text: `Queued ${r.queued} appointment${r.queued === 1 ? '' : 's'} to sync to Google.` }),
    onError: (e) => setGcalMsg({ type: 'error', text: getErrorMessage(e, 'Failed to queue the sync.') }),
  });

  const gcalDisconnectMutation = useMutation({
    mutationFn: disconnectGoogleCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gcal-settings'] });
      queryClient.removeQueries({ queryKey: ['gcal-calendars'] });
      setGcalMsg({ type: 'success', text: 'Google Calendar disconnected.' });
      setTimeout(() => setGcalMsg(null), 4000);
    },
    onError: (e) => setGcalMsg({ type: 'error', text: getErrorMessage(e, 'Failed to disconnect.') }),
  });

  // Handle the OAuth return bounce (?tab=calendar&gcal=connected|error&reason=...).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gcal = params.get('gcal');
    if (!gcal) return;
    if (gcal === 'connected') {
      setGcalMsg({ type: 'success', text: 'Google account connected. Choose a calendar and details below, then Save.' });
      queryClient.invalidateQueries({ queryKey: ['gcal-settings'] });
    } else if (gcal === 'error') {
      setGcalMsg({ type: 'error', text: `Google connection failed: ${params.get('reason') ?? 'unknown error'}.` });
    }
    // Strip the OAuth params so a refresh doesn't re-trigger the banner.
    const url = new URL(window.location.href);
    ['gcal', 'reason', 'tab'].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }, [queryClient]);

  // ─── Render ─────────────────────────────────────────────────────────
  const profileName = `${firstName} ${lastName}`.trim() || profile?.userName || 'User';

  return (
    <div className="space-y-6 p-6">
      <div className="flex gap-6">
        {/* Tab sidebar */}
        <div className="w-48 shrink-0 space-y-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                tab === key ? 'bg-primary/10 text-primary' : 'text-slate-600 hover:bg-slate-50',
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 rounded-xl border border-slate-200 bg-white p-6">

          {/* ── Profile Tab ─────────────────────────────────────────── */}
          {tab === 'profile' && (
            <div className="space-y-6 max-w-2xl">
              <h2 className="text-lg font-semibold text-slate-800">Personal Details</h2>

              {profileMsg && <SuccessBanner message={profileMsg} />}
              {profileMutation.isError && <ErrorBanner message="Failed to save profile." />}

              {profileLoading ? (
                <p className="text-sm text-slate-500">Loading...</p>
              ) : (
                <>
                  <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <UserAvatar
                      name={profileName}
                      photoUrl={profile?.avatarUrl}
                      size="xl"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800">Profile Photo</p>
                      <p className="mt-1 text-xs text-slate-500">
                        JPG, PNG, or GIF. Max 15 MB.
                      </p>
                      {photoMsg && (
                        <p className={cn(
                          'mt-2 text-xs',
                          photoMsg.type === 'success' ? 'text-green-600' : 'text-red-600',
                        )}
                        >
                          {photoMsg.text}
                        </p>
                      )}
                    </div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
                      className="hidden"
                      onChange={(event) => {
                        handlePhotoSelected(event.target.files?.[0]);
                        event.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={photoMutation.isPending}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <Upload className="h-4 w-4" />
                      {photoMutation.isPending ? 'Uploading...' : 'Upload Photo'}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <FieldLabel htmlFor="firstName">First Name</FieldLabel>
                      <TextInput id="firstName" value={firstName} onChange={setFirstName} />
                    </div>
                    <div>
                      <FieldLabel htmlFor="suffix">Suffix</FieldLabel>
                      <TextInput id="suffix" value={suffix} onChange={setSuffix} placeholder="e.g. MD, Jr." />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <FieldLabel htmlFor="lastName">Last Name</FieldLabel>
                      <TextInput id="lastName" value={lastName} onChange={setLastName} />
                    </div>
                    <div>
                      <FieldLabel htmlFor="title">Title</FieldLabel>
                      <TextInput id="title" value={title} onChange={setTitle} placeholder="e.g. Doctor, Nurse" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <FieldLabel htmlFor="settingsEmail">E-Mail</FieldLabel>
                      <TextInput id="settingsEmail" type="email" value={email} onChange={setEmail} />
                    </div>
                    <div>
                      <FieldLabel htmlFor="phone">Phone</FieldLabel>
                      <TextInput id="phone" value={phone} onChange={setPhone} />
                    </div>
                  </div>

                  <div>
                    <FieldLabel>Username</FieldLabel>
                    <TextInput id="username" value={profile?.userName ?? ''} disabled />
                  </div>

                  <div>
                    <FieldLabel>Password</FieldLabel>
                    <button
                      type="button"
                      onClick={() => setTab('password')}
                      className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      Change Password
                    </button>
                  </div>

                  <div className="border-t border-slate-200 pt-5">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">E-mail Signature</h3>
                    <div className="mb-3">
                      <FieldLabel htmlFor="signatureEnabled">Signature Status</FieldLabel>
                      <SelectInput
                        id="signatureEnabled"
                        value={signatureEnabled}
                        onChange={setSignatureEnabled}
                        options={[
                          { value: '0', label: 'Disabled' },
                          { value: '1', label: 'Enabled' },
                        ]}
                      />
                    </div>
                    <div>
                      <FieldLabel>Signature Content</FieldLabel>
                      <TiptapEditor
                        content={emailSignature}
                        onChange={(html) => setEmailSignature(html)}
                        className="min-h-[150px]"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => profileMutation.mutate()}
                    disabled={profileMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {profileMutation.isPending ? 'Saving...' : 'Save Info'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── Password Tab ────────────────────────────────────────── */}
          {tab === 'password' && (
            <div className="space-y-5 max-w-lg">
              <h2 className="text-lg font-semibold text-slate-800">Change Password</h2>

              {pwMsg && (
                pwMsg.type === 'success'
                  ? <SuccessBanner message={pwMsg.text} />
                  : <ErrorBanner message={pwMsg.text} />
              )}

              <div>
                <FieldLabel htmlFor="newPw">New Password</FieldLabel>
                <TextInput id="newPw" type="password" value={newPw} onChange={setNewPw} />
              </div>

              <div>
                <FieldLabel htmlFor="confirmPw">Verify New Password</FieldLabel>
                <TextInput id="confirmPw" type="password" value={confirmPw} onChange={setConfirmPw} />
                {confirmPw && newPw !== confirmPw && (
                  <p className="mt-1 text-xs text-red-500">Passwords do not match.</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => passwordMutation.mutate()}
                disabled={passwordMutation.isPending || !newPw || newPw.length < 6 || newPw !== confirmPw}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                <Lock className="h-4 w-4" />
                {passwordMutation.isPending ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          )}

          {/* ── Notifications Tab ───────────────────────────────────── */}
          {tab === 'notifications' && (
            <div className="space-y-6 max-w-3xl">
              <h2 className="text-lg font-semibold text-slate-800">Notification Preferences</h2>

              {notifMsg && <SuccessBanner message={notifMsg} />}
              {notifMutation.isError && <ErrorBanner message="Failed to save preferences." />}

              {notifLoading ? (
                <p className="text-sm text-slate-500">Loading...</p>
              ) : (
                <>
                  {/* Messaging section */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Messaging</h3>
                    <div className="border-t border-slate-200 pt-3">
                      <div className="max-w-xs">
                        <FieldLabel htmlFor="ccOnSystemReplies">CC me on replies</FieldLabel>
                        <SelectInput
                          id="ccOnSystemReplies"
                          value={notifs.ccOnSystemReplies}
                          onChange={(v) => setNotif('ccOnSystemReplies', v)}
                          options={CC_OPTIONS}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notifications grid */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Notifications</h3>
                    <div className="border-t border-slate-200 pt-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {([
                          ['receivedLead', 'Lead Received'],
                          ['receivedLeadassign', 'Lead Assigned'],
                          ['receivedEmail', 'Email Received'],
                          ['receivedSms', 'SMS Received'],
                          ['receivedMessage', 'Message Received'],
                          ['paymentDeclined', 'Pay Decline/Dispute'],
                          ['paymentReceived', 'Payment Received'],
                          ['receivedLab', 'Lab Received'],
                          ['receivedConsult', 'Consult Received'],
                          ['rescheduleRequest', 'Appt. Re-schedule'],
                          ['receivedQa', 'Q/A Received'],
                          ['noteAlert', 'Patient Note Alert'],
                          ['eformAlert', 'E-Form Received'],
                          ['clearanceAlert', 'Medical Clearance'],
                          ['surgeonNoteAlert', 'Surgeon Note'],
                          ['appointmentStatus', 'Appointment Status'],
                          ...(showAxiomSupport ? [['axiomSupport', 'Axiom Support']] : []),
                          ...(showMembershipCancelled ? [['membershipCancelled', 'Membership Cancelled']] : []),
                        ] as [keyof NotificationPreferences, string][]).map(([key, label]) => (
                          <div key={key}>
                            <FieldLabel htmlFor={key}>{label}</FieldLabel>
                            <SelectInput
                              id={key}
                              value={notifs[key]}
                              onChange={(v) => setNotif(key, v)}
                              options={NOTIF_OPTIONS}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => notifMutation.mutate()}
                    disabled={notifMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {notifMutation.isPending ? 'Saving...' : 'Save Preferences'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── Calendar Sync Tab ───────────────────────────────────── */}
          {tab === 'calendar' && (
            <div className="space-y-6 max-w-2xl">
              <h2 className="text-lg font-semibold text-slate-800">Google Calendar Sync</h2>
              <p className="text-sm text-slate-500">
                Connect your Google account to automatically sync your appointments to a Google calendar.
              </p>

              {gcalMsg && (gcalMsg.type === 'success'
                ? <SuccessBanner message={gcalMsg.text} />
                : <ErrorBanner message={gcalMsg.text} />)}

              {gcalLoading ? (
                <p className="text-sm text-slate-500">Loading...</p>
              ) : !gcalConnected ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center">
                  <p className="mb-4 text-sm text-slate-600">Your Google account is not connected.</p>
                  <button
                    type="button"
                    onClick={() => gcalConnectMutation.mutate()}
                    disabled={gcalConnectMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                  >
                    <LinkIcon className="h-4 w-4" />
                    {gcalConnectMutation.isPending ? 'Redirecting...' : 'Connect Google Calendar'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                    <span className="flex items-center gap-2 text-sm text-green-700">
                      <Check className="h-4 w-4" /> Google account connected
                    </span>
                    <button
                      type="button"
                      onClick={() => gcalDisconnectMutation.mutate()}
                      disabled={gcalDisconnectMutation.isPending}
                      className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      {gcalDisconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  </div>

                  <div>
                    <FieldLabel htmlFor="gcalCalendar">Target Calendar</FieldLabel>
                    {gcalCalendarsLoading ? (
                      <p className="text-sm text-slate-500">Loading calendars...</p>
                    ) : (
                      <SelectInput
                        id="gcalCalendar"
                        value={gcalCalendarId}
                        onChange={setGcalCalendarId}
                        options={[
                          { value: '', label: 'Select a calendar...' },
                          ...(gcalCalendars ?? []).map((c) => ({
                            value: c.id,
                            label: c.primary ? `${c.summary} (primary)` : c.summary,
                          })),
                        ]}
                      />
                    )}
                    <p className="mt-1 text-xs text-slate-500">
                      Appointments assigned to you are added to this calendar.
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-3 text-sm font-semibold text-slate-800">Included Details</h3>
                    <div className="grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
                      {GCAL_FLAG_LABELS.map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={gcalFlags[key]}
                            onChange={(e) => setGcalFlags((prev) => ({ ...prev, [key]: e.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => gcalSaveMutation.mutate()}
                      disabled={gcalSaveMutation.isPending}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      {gcalSaveMutation.isPending ? 'Saving...' : 'Save Info'}
                    </button>
                    <button
                      type="button"
                      onClick={() => gcalSyncMutation.mutate()}
                      disabled={gcalSyncMutation.isPending || !gcalSettings?.calendarId}
                      title={!gcalSettings?.calendarId ? 'Choose a calendar and Save first' : undefined}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <RefreshCw className="h-4 w-4" />
                      {gcalSyncMutation.isPending ? 'Queuing...' : 'Sync Now'}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    New and changed appointments sync automatically. "Sync Now" queues your existing upcoming appointments.
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── Front Desk Reader Tab ────────────────────── */}
          {tab === 'frontdesk' && <FrontDeskReaderTab />}
        </div>
      </div>
    </div>
  );
}
